/**
 * The domain page, rebuilt in this system.
 *
 * The application opens this page on a navy panel with two radial glows and a
 * "Domain Command Center" badge, then runs a five step pipeline whose labels
 * are Schema, Alt Files, Deploy, Verify Page and Monitor, each in one of seven
 * colour-coded states. None of that survives. A person on this page wants
 * three answers: what is this site, is it alright, and what is happening to it.
 */
import type { ReactNode } from 'react'
import { Check } from 'lucide-react'
import { cn } from '@/utils/cn'
import { useArrived } from './media'
import './domain.css'

/* ---------------- The page head ---------------- */

export function DomainHead({
  name,
  host,
  status,
  actions,
}: {
  name: string
  host: string
  status?: ReactNode
  actions?: ReactNode
}) {
  return (
    <div className="ds-dhead">
      <span className="ds-dhead__mark" aria-hidden="true">
        {name.slice(0, 1).toUpperCase()}
      </span>
      <div className="ds-dhead__id">
        <div className="ds-dhead__row">
          <h1 className="ds-h2">{name}</h1>
          {status}
        </div>
        <p className="ds-dhead__host">{host}</p>
      </div>
      {actions && <div className="ds-dhead__actions">{actions}</div>}
    </div>
  )
}

/* ---------------- Tabs ---------------- */

export interface TabItem {
  id: string
  label: string
  /** Shown as an amber count when something on that tab needs a person. */
  count?: number
}

export function TabNav({ items, value, onChange }: { items: TabItem[]; value: string; onChange: (id: string) => void }) {
  return (
    <div className="ds-tabs" role="tablist" aria-label="Sections of this site">
      {items.map((t) => (
        <button
          key={t.id}
          type="button"
          role="tab"
          className="ds-tab"
          aria-current={t.id === value || undefined}
          aria-selected={t.id === value}
          onClick={() => onChange(t.id)}
        >
          {t.label}
          {t.count ? <span className="ds-tab__count">{t.count}</span> : null}
        </button>
      ))}
    </div>
  )
}

/* ---------------- The pipeline ---------------- */

export type StepState = 'done' | 'doing' | 'waiting' | 'needs-you' | 'locked'

export interface PipeStep {
  label: string
  note?: string
  state: StepState
}

/**
 * What is happening to a page, end to end. The application draws this as five
 * tiles in seven colour-coded states with names only we use. Here there are
 * four things a person can act on: it is done, it is happening now, it is
 * waiting its turn, or it needs them. Locked is the fifth and says so plainly.
 */
export function Pipeline({ steps }: { steps: PipeStep[] }) {
  return (
    <div className="ds-pipe" role="list" aria-label="What is happening to this page">
      {steps.map((s, i) => (
        <div key={s.label} className="ds-pipe__step" data-state={s.state} role="listitem">
          <span className="ds-pipe__marker" aria-hidden="true">
            {s.state === 'done' ? <Check /> : i + 1}
          </span>
          <div className="ds-pipe__body">
            <div className="ds-pipe__label">{s.label}</div>
            {s.note && <p className="ds-pipe__note">{s.note}</p>}
          </div>
        </div>
      ))}
    </div>
  )
}

/* ---------------- A row of numbers ---------------- */

export function Split({ children }: { children: ReactNode }) {
  return <div className="ds-split">{children}</div>
}

export function SplitCell({ label, value, note }: { label: ReactNode; value: ReactNode; note?: ReactNode }) {
  return (
    <div className="ds-split__cell">
      <div className="ds-split__label">{label}</div>
      <div className="ds-split__value">{value}</div>
      {note && <div className="ds-split__note">{note}</div>}
    </div>
  )
}

/* ---------------- An allowance ---------------- */

export function Meter({ label, used, of }: { label: string; used: number; of: number }) {
  const pct = of > 0 ? Math.min(100, Math.round((used / of) * 100)) : 0
  return (
    <div className="ds-meter" data-full={used >= of || undefined}>
      <div className="ds-meter__head">
        <span>{label}</span>
        <span>
          <b>
            {used} of {of}
          </b>
        </span>
      </div>
      <div className="ds-meter__track">
        <span className="ds-meter__fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

/* ---------------- A score, drawn round ---------------- */

/** Ink, like every other measurement. Amber only when the number means somebody should look. */
export function Dial({ value, size = 64, lookBelow = 60 }: { value: number; size?: number; lookBelow?: number }) {
  const pct = Math.max(0, Math.min(100, value))
  const stroke = 5
  const r = size / 2 - stroke / 2 - 1
  const c = 2 * Math.PI * r
  /* The arc draws itself once, when the dial comes into view. See useArrived. */
  const [ref, arrived] = useArrived<HTMLDivElement>()
  return (
    <div
      ref={ref}
      className="ds-dial"
      style={{ width: size, height: size, ['--ds-dial-len' as string]: c }}
      data-look={pct < lookBelow || undefined}
      data-arrived={arrived || undefined}
      aria-label={`${Math.round(pct)} out of 100`}
    >
      <svg width={size} height={size} aria-hidden="true">
        <circle className="ds-dial__track" cx={size / 2} cy={size / 2} r={r} strokeWidth={stroke} />
        <circle
          className="ds-dial__fill"
          cx={size / 2}
          cy={size / 2}
          r={r}
          strokeWidth={stroke}
          strokeDasharray={c}
          strokeDashoffset={arrived ? c * (1 - pct / 100) : c}
        />
      </svg>
      <span className="ds-dial__value">{Math.round(pct)}</span>
    </div>
  )
}

/* ---------------- A toolbar above a table ---------------- */

export function Toolbar({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('ds-toolbar', className)}>{children}</div>
}
